/**
 * WidgetDemo - Demo component for the chat widget
 * Embeds the widget dev server in an iframe for testing
 */

import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/Card'
import { ExternalLink, RefreshCw, AlertCircle } from 'lucide-react'

const WIDGET_DEV_URL = 'http://localhost:5301'

export function WidgetDemo() {
  const [iframeKey, setIframeKey] = useState(0)
  const [primaryColor, setPrimaryColor] = useState('#10b981')
  const [position, setPosition] = useState<'bottom-right' | 'bottom-left'>('bottom-right')
  const [greeting, setGreeting] = useState('Hi there! 👋 How can we help you today?')

  const reloadIframe = () => {
    setIframeKey(prev => prev + 1)
  }

  // Build URL with config params - use embed.html for cleaner preview
  const iframeUrl = `${WIDGET_DEV_URL}/embed.html?color=${encodeURIComponent(primaryColor)}&position=${position}&greeting=${encodeURIComponent(greeting)}&t=${iframeKey}`

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="size-5 text-primary mt-0.5" />
            <div className="space-y-1">
              <p className="font-medium">Widget Dev Server Required</p>
              <p className="text-sm text-muted-foreground">
                To see the widget preview, run the widget dev server:
              </p>
              <code className="block mt-2 px-3 py-2 bg-background rounded text-sm font-mono">
                cd widget && pnpm dev
              </code>
              <p className="text-sm text-muted-foreground mt-2">
                This starts the widget on port 5301. The widget has its own Vite build with Preact for minimal bundle size.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration Panel */}
        <Card>
          <CardHeader>
            <CardTitle>Widget Configuration</CardTitle>
            <CardDescription>
              Customize the widget appearance and behavior
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="primaryColor">Primary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="primaryColor"
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="flex-1 font-mono"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="position">Position</Label>
              <select
                id="position"
                value={position}
                onChange={(e) => setPosition(e.target.value as typeof position)}
                className="w-full h-10 px-3 rounded-md border bg-background cursor-pointer"
              >
                <option value="bottom-right">Bottom Right</option>
                <option value="bottom-left">Bottom Left</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="greeting">Greeting Message</Label>
              <Input
                id="greeting"
                type="text"
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={reloadIframe} variant="outline" className="flex-1 cursor-pointer">
                <RefreshCw className="size-4" />
                Reload
              </Button>
              <Button
                variant="outline"
                className="cursor-pointer"
                onClick={() => window.open(WIDGET_DEV_URL, '_blank')}
              >
                <ExternalLink className="size-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Widget Preview */}
        <div className="lg:col-span-2">
          <Card className="h-full">
            <CardHeader className="pb-2">
              <CardTitle>Widget Preview</CardTitle>
              <CardDescription>
                Live preview of the chat widget (click the chat button)
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="relative bg-muted/30 rounded-b-lg overflow-hidden" style={{ height: '600px' }}>
                <iframe
                  key={iframeKey}
                  src={iframeUrl}
                  className="w-full h-full border-0"
                  title="Widget Preview"
                  sandbox="allow-scripts allow-same-origin allow-popups"
                />
                {/* Overlay for iframe loading state */}
                <div className="absolute inset-0 flex items-center justify-center bg-muted/50 pointer-events-none opacity-0 transition-opacity" id="iframe-loading">
                  <div className="text-center">
                    <RefreshCw className="size-8 animate-spin text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Loading widget...</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Embed Code */}
      <Card>
        <CardHeader>
          <CardTitle>Embed Code</CardTitle>
          <CardDescription>
            Add this snippet to your website to install the Banshee widget
          </CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="bg-muted/50 p-4 rounded-lg overflow-x-auto text-sm font-mono">
{`<!-- Banshee Chat Widget -->
<script>
  (function(w,d,s,o,f,js,fjs){
    w['Banshee']=o;w[o]=w[o]||function(){(w[o].q=w[o].q||[]).push(arguments)};
    js=d.createElement(s);fjs=d.getElementsByTagName(s)[0];
    js.async=1;js.src=f;fjs.parentNode.insertBefore(js,fjs);
  })(window,document,'script','banshee','https://scream.banshee.chat/widget/widget.js');

  banshee('init', {
    workspaceId: 'YOUR_WORKSPACE_ID',
    primaryColor: '${primaryColor}',
    position: '${position}',
    greeting: '${greeting}'
  });

  // Optional: Identify logged-in users
  // banshee('identify', { email: 'user@example.com', name: 'John Doe' });
</script>`}
          </pre>
        </CardContent>
      </Card>

      {/* Architecture Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">~2KB</div>
              <div className="text-sm text-muted-foreground mt-1">Loader Script</div>
              <p className="text-xs text-muted-foreground mt-2">
                Tiny script that creates the launcher button and queues commands
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">~50KB</div>
              <div className="text-sm text-muted-foreground mt-1">Widget Bundle</div>
              <p className="text-xs text-muted-foreground mt-2">
                Full Preact chat UI, lazy-loaded on first click
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">0ms</div>
              <div className="text-sm text-muted-foreground mt-1">Initial Blocking</div>
              <p className="text-xs text-muted-foreground mt-2">
                Async loading means zero impact on page load time
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default WidgetDemo
